import Vue from 'vue'
import axios from 'axios'

/**
 * Caisse V3 store — the single source of truth for the new POS page.
 *
 * Design constraint (verified): the legacy checkout mutation endpoints return
 * `redirect()->back()` (HTML), NOT JSON, and the cart lives in DB Order/OrderItem rows.
 * So every action here POSTs/GETs the SAME existing endpoint, treats any resolved
 * response as success, then calls `refreshState()` (GET caisseV3State) to re-hydrate
 * the lines + totals from the server. No checkout business logic is duplicated.
 */
export function createCaisseStore(init = {}) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

    const store = Vue.observable({
        // --- config (from blade props) ---
        routes: init.routes || {},
        token: init.token || null,
        stripeKey: init.stripeKey || '',
        categories: init.categories || {},
        customers: Array.isArray(init.customers) ? init.customers : [],
        staffs: Array.isArray(init.staffs) ? init.staffs : [],
        paymentMethods: Array.isArray(init.paymentMethods) ? init.paymentMethods : [],
        promoCodes: Array.isArray(init.promoCodes) ? init.promoCodes : [],
        perms: {
            create: init.canCreateOrder === true || init.canCreateOrder === 'true',
            modify: init.canModifyOrder === true || init.canModifyOrder === 'true',
            delete: init.canDeleteOrder === true || init.canDeleteOrder === 'true',
        },

        // --- session / context ---
        userId: init.userId || null,
        establishmentId: init.establishmentId || 1,
        currentCustomer: init.currentCustomer || null,

        // --- cart (re-hydrated from caisseV3State) ---
        orderId: null,
        lines: [],
        drafts: [], // voucher drafts (gift cards being issued)
        total: 0,
        count: 0,
        isClotured: false,
        walletBalance: 0,
        loyaltyPoints: 0,
        fidelityRate: 0, // loyalty points per 1 €
        loyaltyEnabled: false,
        walletPercent: 0,
        remisePrice: 0, // order->remise_price (server-applied promo discount)
        promoCode: null,
        // Promotion actually persisted on the order, derived server-side from the promotions_orders
        // pivot. `discountTotal` (NOT remisePrice) is what the UI does math with: it excludes orphan
        // pivot rows and is null/0 on an avoir. `total` stays gross; `totalNet` = total - discountTotal.
        promotion: null,
        discountTotal: 0,
        totalNet: 0,
        orderComment: '', // order->comment (global note)
        currentPrice: 0, // already-paid amount (order->current_price)
        invoices: [], // paid invoices (déjà payé / refundable)

        // --- open / waiting sales (Ventes ouvertes drawer) ---
        openSalesClientName: null,
        openSalesClient: [], // open orders of the selected customer
        openSalesGlobal: [], // recent open orders of the establishment
        openSalesMax: 10,
        openSalesCount: 0,

        // --- ui ---
        loading: false,
        busy: false, // a mutation is in-flight (guards double-submit)
    })

    // Sentinelle de refus : l'appelant la reconnaît et affiche SON message traduit.
    const CLOSED_SALE = 'CLOSED_SALE'

    // ---- helpers (closure, not reactive) -------------------------------------
    const url = (key, params = {}) => {
        let u = store.routes[key] || ''
        Object.keys(params).forEach((k) => {
            u = u.replace(k, encodeURIComponent(params[k]))
        })
        return u
    }
    const body = (data = {}) => {
        const p = new URLSearchParams()
        p.append('_token', csrf)
        Object.keys(data).forEach((k) => {
            const v = data[k]
            if (v !== undefined && v !== null) p.append(k, v)
        })
        return p
    }
    const cfg = {
        headers: { 'X-CSRF-TOKEN': csrf, 'X-Requested-With': 'XMLHttpRequest' },
        // Mutation endpoints answer with 302 -> 200(HTML); never reject on that.
        maxRedirects: 5,
        validateStatus: (s) => s >= 200 && s < 400,
    }

    // First add (no order yet) redirects to `.../caisse?order={id}` — capture that id so all
    // subsequent calls target the same order. Browsers expose the post-redirect URL on responseURL.
    const captureOrderId = (res) => {
        try {
            const u = res?.request?.responseURL || ''
            const m = u.match(/[?&]order=(\d+)/)
            if (m) store.orderId = parseInt(m[1], 10)
        } catch (e) {
            /* noop */
        }
    }

    const canMutate = () => (store.orderId ? store.perms.modify : store.perms.create)

    // Run a mutation then re-hydrate. Guards concurrent / double submits.
    const mutate = async (fn) => {
        if (store.busy) return
        store.busy = true
        try {
            await fn()
            await store.refreshState()
        } finally {
            store.busy = false
        }
    }

    // Same, but refused on a CLOSED (clôturée) order — NF525: a signed/closed order can never be
    // modified. The real gate is server-side (OrderItemObserver throws on creating/updating/deleting
    // when closed_at / signature / status_id === 7 and logs a JET integrity event); this is only the
    // affordance, so the cashier gets a clear message instead of a 500.
    //
    // A closed sale is FULLY read-only from the caisse — cart edits AND refunds (refundInvoice) both go
    // through here. Refunds/avoirs on a closed sale are issued from the invoices page instead.
    // openSale() stays on plain mutate(): opening another sale is navigation, not a mutation.
    const mutateCart = async (fn) => {
        if (store.isClotured) return
        return mutate(fn)
    }

    // True when the loaded order is read-only (clôturée). Cart edits must be disabled.
    store.isLocked = () => !!store.isClotured

    // ---- read --------------------------------------------------------------
    store.refreshState = async function () {
        const res = await axios.get(url('state'), {
            params: { order: store.orderId || undefined },
            ...cfg,
        })
        const d = res.data || {}
        if (d.orderId) store.orderId = d.orderId
        store.lines = Array.isArray(d.lines) ? d.lines : []
        store.drafts = Array.isArray(d.drafts) ? d.drafts : []
        store.total = d.total || 0
        store.count = d.count || 0
        store.isClotured = !!d.isClotured
        store.walletBalance = d.walletBalance || 0
        store.loyaltyPoints = d.loyaltyPoints || 0
        store.fidelityRate = d.fidelityRate || 0
        store.loyaltyEnabled = !!d.loyaltyEnabled
        store.walletPercent = d.walletPercent || 0
        store.remisePrice = d.remisePrice || 0
        store.promoCode = d.promoCode || null
        store.promotion = d.promotion || null
        store.discountTotal = d.discountTotal || 0
        store.totalNet = typeof d.totalNet === 'number' ? d.totalNet : store.total
        store.orderComment = d.orderComment || ''
        store.currentPrice = d.currentPrice || 0
        store.invoices = Array.isArray(d.invoices) ? d.invoices : []
        // Keep the selected customer enriched with full contact (email/phone) — the single source of
        // truth used to prefill the "Offert par" voucher form, whatever the selection path.
        if (d.customer) store.currentCustomer = d.customer
        return d
    }

    // ---- catalog browse ----------------------------------------------------
    store.browsePresta = async function (subcategoryId, { isVoucher = false, undatedTicket = false } = {}) {
        const res = await axios.get(url('prestaBySubcategory'), {
            params: { id: subcategoryId, isVoucher: isVoucher || undatedTicket ? 1 : -1, undatedTicket: undatedTicket ? 1 : 0 },
            ...cfg,
        })
        return res?.data?.data ?? res.data ?? []
    }

    // Tag-grouped experience catalog (validated prestations, shown online-or-not — NO visible_online filter).
    // Returns { tags: [{ id, name, image, prestations: [...] }], untagged: [...] }. Prestations have the same
    // shape as browsePresta (raw model + prices_by_times), so the item cards / duration popover / addPresta
    // all keep working unchanged.
    store.loadCatalogByTag = async function ({ undatedTicket = false } = {}) {
        if (!store.routes.catalogByTag) return { tags: [], untagged: [] }
        const res = await axios.get(url('catalogByTag'), { params: { undatedTicket: undatedTicket ? 1 : 0 }, ...cfg })
        const d = res?.data?.data ?? res?.data ?? {}
        return { tags: Array.isArray(d.tags) ? d.tags : [], untagged: Array.isArray(d.untagged) ? d.untagged : [] }
    }
    store.browseProducts = async function (subcategoryId) {
        const res = await axios.get(url('productsBySubcategory'), { params: { id: subcategoryId }, ...cfg })
        return res.data || []
    }
    // All products of the establishment (each with its `subcategory`), chip-filtered by category client-side.
    store.browseAllProducts = async function () {
        if (!store.routes.products) return []
        const res = await axios.get(url('products'), { params: { all: 1 }, ...cfg })
        return Array.isArray(res.data) ? res.data : res.data || []
    }
    store.research = async function (name, category = null) {
        const res = await axios.get(url('research'), { params: { name, category }, ...cfg })
        return res.data || []
    }

    // ---- add to cart -------------------------------------------------------
    // Mirrors the exact field names the legacy <form> posts to addPrestaCheckout.
    store.addPresta = function (payload = {}) {
        return mutateCart(async () => {
            const data = {
                type: payload.type || 'Activité',
                item_id: payload.item_id,
                user_id: store.userId,
                order_id: store.orderId || undefined,
                prestationLabel: payload.label || '',
                quantity: payload.quantity || 1,
                buttonClicked: payload.timeOption ? 'validateButton' : 'mainButton',
                priceModalPresta: payload.price,
                priceModalPrestaHT: payload.price_ht,
                prestationPriceMain: payload.price,
                prestationPriceHtMain: payload.price_ht,
                price_ht: payload.price_ht,
            }
            if (payload.timeOption) {
                data.timeQty = payload.timeOption.duration
                data.priceByTimesId = payload.timeOption.id
                data.priceByTimes = payload.timeOption.price
                data.priceModalPresta = payload.timeOption.price
                data.priceModalPrestaHT = payload.timeOption.price_ht
            }
            const res = await axios.post(url('addPresta'), body(data), cfg)
            captureOrderId(res)
        })
    }

    // Billet simple : ajoute EN UNE FOIS le parent (prix 0, qté = somme) + une ligne par variante
    // choisie (parent_id), comme la V2. Renvoie JSON { status, order_id } — pas de redirection, donc on
    // capte l'id directement. `options` = [{ id, quantity }] ; on l'aplatit en options[i][…] pour que
    // Laravel le reçoive comme un tableau. Le prix est décidé côté serveur, jamais envoyé d'ici.
    store.addSimpleTicket = function ({ parentId, options = [] } = {}) {
        return mutateCart(async () => {
            if (!store.routes.addSimpleTicket) throw new Error('addSimpleTicket route missing')
            const flat = { parent_id: parentId, user_id: store.userId, order_id: store.orderId || undefined }
            options.forEach((o, i) => {
                flat[`options[${i}][id]`] = o.id
                flat[`options[${i}][quantity]`] = o.quantity
            })
            const res = await axios.post(url('addSimpleTicket'), body(flat), cfg)
            if (res?.data?.order_id) store.orderId = res.data.order_id
        })
    }

    // Vendre un bowling au comptoir : effectif + formule, rien d'autre.
    //
    // C'est une LIGNE SÈCHE (décision produit 2026-09-18) : aucune piste bloquée, aucun créneau —
    // la vraie réservation reste au front-office et au calendrier BO. On n'envoie ni prix ni
    // espace : le serveur résout le palier de l'effectif et décide du prix, comme en ligne.
    //
    // Un refus (effectif hors bornes, vente clôturée, client non sélectionné) revient avec son
    // message serveur : on le laisse remonter pour que l'appelant l'affiche tel quel. Retourne
    // `true` quand la ligne est écrite, `null` si la mutation n'a pas eu lieu (double-clic) — le
    // panier rafraîchi par `mutateCart` est la seule source de vérité sur ce qui vient d'être vendu.
    store.addBooking = async function ({ prestationId, durationId, participants, note = '' } = {}) {
        if (!store.routes.addBooking) throw new Error('addBooking route missing')
        // Refus EXPLICITES, et non un silence : `mutateCart` et `mutate` sortent sans rien faire
        // sur une vente clôturée ou un double-clic. Rendre `[]` dans ces cas était indiscernable
        // d'une ligne ajoutée, et l'appelant affichait un succès en vert.
        if (store.isClotured) throw new Error(CLOSED_SALE)
        if (store.busy) return null

        let result = false
        await mutateCart(async () => {
            const res = await axios.post(
                url('addBooking'),
                body({
                    prestationId,
                    durationId,
                    participants,
                    note,
                    order: store.orderId || undefined,
                }),
                { ...cfg, validateStatus: (s) => s >= 200 && s < 500 }
            )
            if (res?.status >= 400) {
                // Message vide = l'appelant choisira son propre repli traduit ; on ne code aucun
                // texte en dur ici, le store ne connaît pas la langue de l'utilisateur.
                throw new Error(res?.data?.message || '')
            }
            if (res?.data?.order_id) store.orderId = res.data.order_id
            result = true
        })

        return result
    }

    // Ajuster l'EFFECTIF d'une ligne bowling sèche du panier : PAS un ±1 générique (incQty /
    // decQty touchent la quantité à prix unitaire constant) — le serveur re-résout PALIER
    // (prix unitaire) et durée effective aux mêmes arguments que l'ajout (parité réservation
    // en ligne), puis mutateCart rafraîchit le panier, qui EST la seule source de vérité sur
    // le prix et la durée qui viennent d'être vendus. Un override manuel du prix est écrasé
    // par le palier (décision produit : la parité fait foi).
    // Refus traduit → Error(message) pour toast ; `null` = double-clic ; CLOSED_SALE = clôturée.
    store.setBookingQty = async function (rowId, participants) {
        if (!store.routes.updateBookingQty) throw new Error('updateBookingQty route missing')
        if (store.isClotured) throw new Error(CLOSED_SALE)
        if (store.busy) return null

        let result = false
        await mutateCart(async () => {
            const res = await axios.post(url('updateBookingQty'), body({ orderItem: rowId, participants }), {
                ...cfg,
                validateStatus: (s) => s >= 200 && s < 500,
            })
            if (res?.status >= 400) {
                // Message vide = l'appelant choisira son propre repli traduit ; le store ne
                // connaît pas la langue de l'utilisateur.
                throw new Error(res?.data?.message || '')
            }
            result = true
        })

        return result
    }

    // Product add returns JSON { status, order_id } — capture the order id directly.
    // With an existing cart, use the by-order endpoint (carries order_id) so the product APPENDS to it;
    // addProductInCaisse re-derives the order by status and would spawn a NEW order, wiping the cart.
    // Mirrors the legacy calendar pattern (ApiManager.addToCard / addToCardByOrder).
    store.addProduct = function (productId) {
        return mutateCart(async () => {
            const hasOrder = !!store.orderId
            const payload = { product_id: productId, user_id: store.userId }
            if (hasOrder) payload.order_id = store.orderId
            const res = await axios.post(url(hasOrder ? 'addProductByOrder' : 'addProduct'), body(payload), cfg)
            if (res?.data?.order_id) store.orderId = res.data.order_id
        })
    }

    store.addVoucher = function ({ amount, quantity = 1, label = 'Bon cadeau' } = {}) {
        return mutateCart(async () => {
            const res = await axios.post(
                url('addVoucher'),
                body({
                    type: 'Bons cadeau',
                    bonCadeau: true,
                    amount,
                    quantity,
                    user_id: store.userId,
                    order_id: store.orderId || undefined,
                    prestationLabel: label,
                    priceModalPresta: amount,
                }),
                cfg
            )
            captureOrderId(res)
        })
    }

    // Undated ticket ("Billet non daté") — a voucher draft tied to a Prestation. Reuses voucherAdd with
    // type:'Activité' + bonCadeau:true + the prestation item_id + qtyUndatedTicket (legacy VspService:389).
    store.addUndatedTicket = function ({ itemId, amount, quantity = 1, price, price_ht, timeOption = null } = {}) {
        return mutateCart(async () => {
            const data = {
                type: 'Activité',
                bonCadeau: true,
                item_id: itemId,
                user_id: store.userId,
                order_id: store.orderId || undefined,
                amount,
                qtyUndatedTicket: quantity,
                priceModalPresta: price,
                priceModalPrestaHT: price_ht,
                buttonClicked: timeOption ? 'validateButton' : 'mainButton',
            }
            if (timeOption) {
                data.timeQty = timeOption.duration
                data.priceByTimesId = timeOption.id
                data.priceByTimes = timeOption.price
                data.priceModalPresta = timeOption.price
                data.priceModalPrestaHT = timeOption.price_ht
            }
            const res = await axios.post(url('addVoucher'), body(data), cfg)
            captureOrderId(res)
        })
    }

    store.addWallet = function ({ amount, quantity = 1 } = {}) {
        return mutateCart(async () => {
            const res = await axios.post(
                url('addWallet'),
                body({
                    type: 'Wallet',
                    wallet: true,
                    amount,
                    quantity,
                    user_id: store.userId,
                    order_id: store.orderId || undefined,
                }),
                cfg
            )
            captureOrderId(res)
        })
    }

    // ---- line edits --------------------------------------------------------
    store.incQty = (rowId) => mutateCart(() => axios.get(url('incQty', { ROWID: rowId }), cfg))
    store.decQty = (rowId) => mutateCart(() => axios.get(url('decQty', { ROWID: rowId }), cfg))
    store.setQty = (rowId, quantity) => mutateCart(() => axios.post(url('changeQty', { ROWID: rowId }), body({ quantity }), cfg))
    store.changePrice = (rowId, price) => mutateCart(() => axios.post(url('changePrice', { ROWID: rowId }), body({ price }), cfg))
    store.removeLine = (rowId) => mutateCart(() => axios.get(url('deleteLine', { ROWID: rowId }), cfg))
    store.clearCart = () => mutateCart(() => axios.get(url('deleteAll'), cfg))

    // ---- refund a paid invoice (total or partial) — legacy refoundOrderInvoice {id, amount} ----
    // Refused on a CLOSED order: a clôturée sale is fully read-only from the caisse. Refunds/avoirs
    // on a closed sale are done from the invoices page, not here. Still allowed on an OPEN order that
    // took an acompte (refund a deposit before the sale is closed).
    store.refundInvoice = (id, amount) => mutateCart(() => axios.post(url('refund'), body({ id, amount }), cfg))

    // ---- validate a gift-card draft (recipient binding) — legacy validateVoucher (multipart FormData) ----
    store.validateVoucher = function (payload = {}) {
        return mutateCart(async () => {
            const fd = new FormData()
            fd.append('_token', csrf)
            fd.append('type', 'App\\Models\\Voucher\\Voucher')
            fd.append('order_id', store.orderId)
            Object.keys(payload).forEach((k) => {
                if (payload[k] !== undefined && payload[k] !== null && payload[k] !== '') fd.append(k, payload[k])
            })
            const res = await axios.post(url('voucherValidate'), fd, {
                headers: { 'X-CSRF-TOKEN': csrf, 'X-Requested-With': 'XMLHttpRequest' },
            })
            captureOrderId(res)
        })
    }

    // ---- voucher draft (gift card being issued) edits — operate on the VoucherItem id ----
    store.changeDraftPrice = (vItemId, price) =>
        mutateCart(() => axios.post(url('draftPrice', { ROWID: vItemId }), body({ price }), cfg))
    store.draftInc = (vItemId) => mutateCart(() => axios.post(url('draftInc', { ROWID: vItemId }), body({}), cfg))
    store.draftDesc = (vItemId) => mutateCart(() => axios.post(url('draftDesc', { ROWID: vItemId }), body({}), cfg))
    store.draftSetQty = (vItemId, quantity) =>
        mutateCart(() => axios.post(url('draftQty', { ROWID: vItemId }), body({ quantity }), cfg))
    store.deleteDraft = (vItemId) => mutateCart(() => axios.get(url('draftDelete', { ROWID: vItemId }), cfg))

    // ---- notes (legacy updateOrderItemComment / updateOrderComment) ----
    store.updateLineNote = (rowId, comment) =>
        mutateCart(() => axios.post(url('lineNote', { ROWID: rowId }), body({ comment }), cfg))
    store.updateOrderNote = (comment) =>
        mutateCart(() => axios.post(url('orderNote', { ROWID: store.orderId }), body({ comment }), cfg))

    // ---- per-line seller / adviser (legacy editSeller: writes OrderItem.id_seller / id_advised) ----
    // editSeller sets BOTH columns from the request, so always pass the current "other" value to preserve it.
    // body() omits null/undefined -> a cleared select sends nothing -> the column is set to null server-side.
    store.editSeller = (rowId, sellerId, advisedId) =>
        mutateCart(() =>
            axios.post(url('editSeller', { ROWID: rowId }), body({ seller_id: sellerId, advised_id: advisedId }), cfg)
        )

    // ---- promo code (réduction) ----
    // Reuses /promotion/get/code/{code}?fromBackend=true (PromotionController::getCode):
    // returns the eager-loaded Promotion on success, or the plain STRING "Code promo non valide." on failure.
    store.lookupPromo = async function (code) {
        const res = await axios.get(url('promoLookup', { CODE: code }), {
            ...cfg,
            validateStatus: (s) => s >= 200 && s < 500,
        })
        if (typeof res.data === 'string' || !res.data || !res.data.id) {
            return { ok: false, message: typeof res.data === 'string' ? res.data : 'Code promo invalide' }
        }
        return { ok: true, promo: res.data }
    }

    // Persist the promo on the order NOW (survives a refresh), then re-hydrate so store.promotion /
    // discountTotal / totalNet reflect it. Refused on a closed order (server guards it too). Returns
    // { ok, message } — the caller shows `message` on failure.
    store.applyPromotion = async function (code) {
        if (store.isClotured || !store.orderId) return { ok: false }
        if (store.busy) return { ok: false, reason: 'busy' }
        store.busy = true
        try {
            const res = await axios.post(url('applyPromotion'), body({ order: store.orderId, code }), {
                ...cfg,
                validateStatus: (s) => s >= 200 && s < 500,
            })
            if (!res.data || res.data.ok !== true) {
                return { ok: false, message: (res.data && res.data.message) || 'Code promo invalide' }
            }
            await store.refreshState()
            return { ok: true }
        } finally {
            store.busy = false
        }
    }

    // Apply an ad-hoc "remise instantanée" (fixed € or percentage) the cashier types in directly.
    // The server generates a hidden single-use promotion and applies it — the response/rehydration is
    // identical to applyPromotion(), so store.promotion / discountTotal / totalNet reflect it, but the
    // promotion carries is_special=true and the UI shows its title ("Remise instantanée"), never a code.
    store.applyCustomPromotion = async function (discountType, discountAmount) {
        if (store.isClotured || !store.orderId) return { ok: false }
        if (store.busy) return { ok: false, reason: 'busy' }
        store.busy = true
        try {
            const res = await axios.post(
                url('applyCustomPromotion'),
                body({ order: store.orderId, discount_type: discountType, discount_amount: discountAmount }),
                { ...cfg, validateStatus: (s) => s >= 200 && s < 500 }
            )
            if (!res.data || res.data.ok !== true) {
                return { ok: false, message: (res.data && res.data.message) || 'Remise invalide' }
            }
            await store.refreshState()
            return { ok: true }
        } finally {
            store.busy = false
        }
    }

    // Drop the persisted promo from the order, then re-hydrate.
    store.removePromotion = async function () {
        if (store.isClotured || !store.orderId) return { ok: false }
        if (store.busy) return { ok: false, reason: 'busy' }
        store.busy = true
        try {
            const res = await axios.delete(url('removePromotion'), {
                ...cfg,
                data: body({ order: store.orderId }),
                validateStatus: (s) => s >= 200 && s < 500,
            })
            if (!res.data || res.data.ok !== true) {
                return { ok: false, message: (res.data && res.data.message) || 'Erreur' }
            }
            await store.refreshState()
            return { ok: true }
        } finally {
            store.busy = false
        }
    }

    // ---- customer ----------------------------------------------------------
    store.selectCustomer = function (user) {
        return mutateCart(async () => {
            await axios.get(url('changeCustomer'), { params: { customerSelect: user.id }, ...cfg })
            store.userId = user.id
            store.currentCustomer = user
            // Changing customer switches the active order context — drop the stale order id so
            // refreshState() resolves the new customer's draft (or an empty cart), like the legacy caisse.
            store.orderId = null
        })
    }
    store.newWalkIn = function () {
        return mutateCart(async () => {
            await axios.post(url('addClientDePassage'), body({}), cfg)
            store.orderId = null
        })
    }
    // Reuses legacy addUserByCalendar: returns the new user id, or { errors } (HTTP 200) on validation failure.
    store.createCustomer = async function (payload = {}) {
        if (!store.routes.createCustomer) return { ok: false, message: 'route_missing' }
        try {
            const res = await axios.post(url('createCustomer'), body({ guest: 0, ...payload }), {
                ...cfg,
                validateStatus: (s) => s >= 200 && s < 500,
            })
            const data = res.data
            if (data && typeof data === 'object' && data.errors) return { ok: false, errors: data.errors }
            const id = parseInt(data, 10)
            if (!id || isNaN(id)) return { ok: false, message: 'unexpected_response' }
            return { ok: true, userId: id }
        } catch (e) {
            return { ok: false, message: e?.response?.data?.message || e.message }
        }
    }
    // Customer picker — GET /api/v3/customer/search (Sanctum token), dedicated to the Caisse.
    // Returns flat CustomerResource rows (id/first_name/last_name/email/phone_number), relevance-ordered.
    // `q` empty = the 25 most recent clients; non-empty = server-side search on name / email / phone.
    store.searchCustomers = async function (q = '') {
        const res = await axios.get('/api/v3/customer/search', {
            params: { search: (q || '').trim(), limit: 25 },
            headers: { ...cfg.headers, Authorization: store.token ? `Bearer ${store.token}` : undefined },
        })
        return (res.data?.data ?? []).filter(Boolean)
    }

    // ---- gift card (Bon cadeau) redemption ---------------------------------
    // Reuses /voucher/check-code — returns the Voucher (id, available_amount, code) on success,
    // or { message } on 4xx. We then redeem it via submitBasket's `voucher-used[]` payload.
    store.checkVoucher = async function (code, slot = 1) {
        const res = await axios.get(url('checkVoucher'), {
            params: { code, order: slot, orderId: store.orderId },
            validateStatus: (s) => s >= 200 && s < 500,
            headers: cfg.headers,
        })
        if (res.status >= 400 || (res.data && res.data.message)) {
            return { ok: false, message: res.data?.message || 'Bon cadeau invalide' }
        }
        const v = res.data
        if (!v || !v.id) return { ok: false, message: 'Bon cadeau invalide' }
        return { ok: true, voucher: { id: v.id, code: v.code, available: Number(v.available_amount) || 0 } }
    }

    // ---- open / waiting sales (Ventes ouvertes) ----------------------------
    // Read-only list: the selected customer's open orders + recent global open orders.
    store.fetchOpenSales = async function () {
        // Route may be absent on a partial deploy (blade sends null) — skip cleanly.
        if (!store.routes.openSales) {
            store.openSalesCount = 0
            return null
        }
        try {
            const res = await axios.get(url('openSales'), cfg)
            const d = res.data || {}
            store.openSalesClientName = d.clientName || null
            store.openSalesClient = Array.isArray(d.clientOrders) ? d.clientOrders : []
            store.openSalesGlobal = Array.isArray(d.globalOrders) ? d.globalOrders : []
            store.openSalesMax = d.max || 10
            store.openSalesCount = store.openSalesClient.length + store.openSalesGlobal.length
            return d
        } catch (e) {
            store.openSalesClient = []
            store.openSalesGlobal = []
            store.openSalesCount = 0
            return null
        }
    }
    // Re-open an existing order into the cart: point the session customer at the order's owner,
    // adopt its id, then re-hydrate (state?order=id). Avoids legacy addOrderToBasket (which rebuilds
    // the unused Gloudemans session cart and force-flips the status).
    store.openSale = function (order) {
        return mutate(async () => {
            await axios.get(url('changeCustomer'), { params: { customerSelect: order.userId }, ...cfg })
            store.userId = order.userId
            store.currentCustomer = {
                id: order.userId,
                first_name: order.firstName,
                last_name: order.lastName,
                is_guest: !!order.isGuest,
            }
            store.orderId = order.id
        })
    }

    // ---- prepaid series / treatments the customer already owns --------------
    // Reuses GET /back-office/series_customers/{user}/getAllAvailableSeries (and treatments).
    // {user} is the USER id. Each row carries pivot.id (= series_user/treatments_user id, the
    // userSerie/userTreatment value), pivot.quantity_left (remaining sessions), is_ticket, and
    // prestations[] (each with pivot.prestation_id + pivot.duration) used for cart-line matching.
    store.fetchPrepaid = async function (userId) {
        const uid = userId || store.userId
        if (!uid) return { series: [], treatments: [] }
        const pull = async (key) => {
            try {
                const res = await axios.get(url(key, { USERID: uid }), cfg)
                return Array.isArray(res.data) ? res.data : []
            } catch (e) {
                return []
            }
        }
        const [series, treatments] = await Promise.all([pull('availableSeries'), pull('availableTreatments')])
        return { series, treatments }
    }

    // ---- Stripe (saved card) ------------------------------------------------
    store.loadCards = async function (userId) {
        const res = await axios.get(url('paymentMethodOfUser', { USERID: userId }), cfg)
        return Array.isArray(res.data) ? res.data : []
    }
    store.chargeStripe = async function ({ cartId, montant }) {
        const res = await axios.post(
            url('payByStripe'),
            body({ cartId, orderId: store.orderId, userId: store.userId, montant }),
            { ...cfg, validateStatus: (s) => s >= 200 && s < 500 }
        )
        if (res.status >= 400) return { ok: false, message: res.data?.message || 'Paiement Stripe refusé' }
        return { ok: true }
    }

    // ---- payment / finalize ------------------------------------------------
    // payments = { "Espèces": 30, "CB": 10 } — keys are PaymentMethod.name (matches submitBasket).
    // vouchers = [{ id, amount, type:'Bon cadeau' }] -> sent as voucher-used[] (JSON per entry).
    store.submitSale = async function (
        payments,
        {
            closeSale = true,
            vouchers = [],
            fidelityPoints = 0,
            promo = null,
            prepaidSerie = [],
            prepaidTreatment = [],
            invoice = null, // { send_invoice, company_name, company_address, company_cp, company_country }
            sendMail = false, // legacy "mail de remerciement"
            notice = false, // legacy review-links toggle (inside parameterThanksMail)
        } = {}
    ) {
        if (store.busy) return { ok: false, reason: 'busy' }
        // A clôturée order is fiscally frozen: no further encaissement / clôture on it. Refunds go
        // through refundInvoice(), which is intentionally NOT gated.
        if (store.isClotured) return { ok: false, reason: 'clotured' }
        store.busy = true
        try {
            // promoCodesImpact: array-of-arrays; the server (applyPromoCodes) reads ONLY [i][0].code.code (the
            // textual code) then re-resolves the Promotion + recomputes the discount server-side (order->remise_price).
            // We still send the full legacy-shaped object for fidelity.
            const promoImpact = promo
                ? JSON.stringify([
                      [
                          {
                              id: promo.id,
                              title: promo.title,
                              discount_type: promo.discount_type,
                              discount_amount: promo.discount_amount,
                              enable: true,
                              has_voucher: !!promo.has_voucher,
                              global: !!promo.is_global,
                              code: { code: promo.code, id: promo.id, has_voucher: !!promo.has_voucher, enable: true },
                          },
                      ],
                  ])
                : '[]'
            // submitBasket (unchanged legacy) expects these even when empty:
            //  - parameterThanksMail: json_decode()'d then `->notice` is read UNGUARDED -> must be a JSON object
            //  - fidelity_points: loyalty points spent (raw int); submitBasket converts via fidelity_points_trans
            const data = body({
                order_id: store.orderId,
                close_sale: closeSale ? 'true' : 'false',
                promoCodesImpact: promoImpact,
                parameterThanksMail: JSON.stringify({ notice: !!notice, rebooking: false }),
                fidelity_points: Number(fidelityPoints) || 0,
            })
            // Always send the payment amounts (incl. 0) so submitBasket's `foreach($payments)` gets an
            // array even on a voucher-only / fidelity-only sale (it throws on null). 0-amounts are skipped
            // server-side by the `if ($amount > 0)` guard.
            let appended = 0
            Object.keys(payments || {}).forEach((method) => {
                data.append(`payments[${method}]`, Number(payments[method]) || 0)
                appended++
            })
            if (!appended) data.append('payments[Espèces]', 0)
            ;(vouchers || []).forEach((v) => {
                const amount = Number(v.amount) || 0
                if (amount > 0) data.append('voucher-used[]', JSON.stringify({ id: v.id, amount, type: v.type || 'Bon cadeau' }))
            })
            // Prepaid series / treatments used as payment. submitBasket json_decode()s each element and
            // calls PaymentService::payWithSerie / payWithTreatment (decrements remaining sessions, writes
            // the OrderInvoice). Shape MUST match the legacy Cart.vue producers exactly:
            //   prepaid-serie[]     = { orderItem:<OrderItem id>, userSerie:<series_user.id>,     quantity:<int> }
            //   prepaid-treatment[] = { orderItem:<OrderItem id>, userTreatment:<treatments_user.id>, quantity:<int> }
            ;(prepaidSerie || []).forEach((p) => {
                if (p && p.orderItem && p.userSerie && Number(p.quantity) > 0)
                    data.append(
                        'prepaid-serie[]',
                        JSON.stringify({ orderItem: p.orderItem, userSerie: p.userSerie, quantity: p.quantity })
                    )
            })
            ;(prepaidTreatment || []).forEach((p) => {
                if (p && p.orderItem && p.userTreatment && Number(p.quantity) > 0)
                    data.append(
                        'prepaid-treatment[]',
                        JSON.stringify({ orderItem: p.orderItem, userTreatment: p.userTreatment, quantity: p.quantity })
                    )
            })
            // Facture société + mail de remerciement (legacy CloseSaleModal). submitBasket only acts on these
            // inside its close_sale branch (persists company_* inline 854-861 + InvoicesController::sendInvoice
            // 1098; send_mail dispatches the thank-you mail 1092). Harmless on an acompte.
            if (sendMail) data.append('send_mail', 'on')
            if (invoice && invoice.send_invoice) {
                data.append('send_invoice', 'true')
                data.append('company_name', invoice.company_name || '')
                data.append('company_address', invoice.company_address || '')
                data.append('company_cp', invoice.company_cp || '')
                data.append('company_country', invoice.company_country || '')
            }
            const res = await axios.post(url('submitBasket'), data, cfg)
            // On CLOSE (close_sale=true) submitBasket redirects to the legacy caisse on success, but ->back()
            // (to /v3/caisse) when it catches an error and rolls back — use that to detect a rolled-back close.
            // On an ACOMPTE (close_sale=false) success ALSO returns to /v3, so we can't use the URL there.
            const finalUrl = res?.request?.responseURL || ''
            if (closeSale && /\/v3\/caisse/.test(finalUrl)) {
                return { ok: false, reason: "La vente n'a pas pu être finalisée." }
            }
            return { ok: true }
        } catch (e) {
            return { ok: false, reason: e?.response?.data?.message || e.message }
        } finally {
            store.busy = false
        }
    }

    // Start a brand-new sale (fresh empty cart): forget the current order id and re-hydrate.
    store.newSale = function () {
        store.orderId = null
        store.lines = []
        store.total = 0
        store.count = 0
        store.isClotured = false
        store.promotion = null
        store.discountTotal = 0
        store.totalNet = 0
    }

    store.canMutate = canMutate

    return store
}
